import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Login from "../pages/auth/Login";
import MainLayout from "../components/layout/MainLayout";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import BusinessList from "../pages/business/BusinessList";
import BusinessDetail from "../pages/business/BusinessDetail";
import BusinessFeatures from "../pages/business/BusinessFeatures";
import PaymentGateways from "../pages/business/PaymentGateways";
import CreateBusiness from "../pages/business/CreateBusiness";
import BusinessStores from "../pages/business/BusinessStores";
import StoreDetail from "../pages/business/StoreDetail";
import StoreForm from "../pages/business/StoreForm";
import StoreFeatures from "../pages/business/StoreFeatures";
import LicenseHistory from "../pages/licenses/LicenseHistory";
import EditLicense from "../pages/licenses/EditLicense";
import AllocateLicense from "../pages/licenses/AllocateLicense";
import LicenseExpired from "../pages/licenses/LicenseExpired";
import Categories from "../pages/menu/Categories";
import Items from "../pages/menu/Items";
import Modifiers from "../pages/menu/Modifiers";
import ModifierOptions from "../pages/menu/ModifierOptions";
import Bundles from "../pages/menu/Bundles";
import Promotions from "../pages/menu/Promotions";
import UpsellCampaigns from "../pages/menu/UpsellCampaigns";
import Tags from "../pages/menu/Tags";
import Allergens from "../pages/menu/Allergens";
import ProteinTypes from "../pages/menu/ProteinTypes";
import MenuPreview from "../pages/menu/MenuPreview";
import DayOverrides from "../pages/menu/DayOverrides";
import WeeklyScheduling from "../pages/menu/WeeklyScheduling";
import Reservations from "../pages/menu/Reservations";
import QRConfig from "../pages/settings/QRConfig";
import OrderTypes from "../pages/settings/OrderTypes";
import GeneralSettings from "../pages/settings/GeneralSettings";
import UserList from "../pages/users/UserList";
import UserForm from "../pages/users/UserForm";
import RoleList from "../pages/users/RoleList";
import RoleForm from "../pages/users/RoleForm";
import PermissionList from "../pages/users/PermissionList";
import RolePermissionMatrix from "../pages/users/RolePermissionMatrix";
import UserRoleAssignment from "../pages/users/UserRoleAssignment";
import CompletedOrders from "../pages/reporting/CompletedOrders";
import CompletedOrderDetail from "../pages/reporting/CompletedOrderDetail";
import SalesSummary from "../pages/reporting/SalesSummary";
import PaymentSummary from "../pages/reporting/PaymentSummary";
import HourlyDemand from "../pages/reporting/HourlyDemand";
import TopItemsCategoryMix from "../pages/reporting/TopItemsCategoryMix";
import CashSessionsReport from "../pages/reporting/cashing-up/CashSessionsReport";
import VariancesReport from "../pages/reporting/cashing-up/VariancesReport";
import PaidOutsReport from "../pages/reporting/cashing-up/PaidOutsReport";
import DayClosesReport from "../pages/reporting/cashing-up/DayClosesReport";
import CashSessionDetail from "../pages/reporting/cashing-up/CashSessionDetail";
import VarianceDetail from "../pages/reporting/cashing-up/VarianceDetail";
import PaidOutDetail from "../pages/reporting/cashing-up/PaidOutDetail";
import ScrollToTop from "../components/common/ScrollToTop";

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/license-expired" element={<LicenseExpired />} />

        {/* Business Routes - Super Admin Only */}
        <Route
          path="/businesses"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <BusinessList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/new"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <CreateBusiness />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <BusinessDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <BusinessStores />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores/new"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <StoreForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores/:storeId"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <StoreDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores/:storeId/edit"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <StoreForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores/new/features"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <StoreFeatures />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/stores/:storeId/features"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <StoreFeatures />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/features"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <BusinessFeatures />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/payment-gateways"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <PaymentGateways />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/licenses"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <LicenseHistory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/licenses/new"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <AllocateLicense />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/businesses/:businessId/licenses/edit"
          element={
            <ProtectedRoute requireSuperAdmin>
              <MainLayout>
                <EditLicense />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Menu Routes - Non-Super Admin Only */}
        <Route
          path="/menu/categories"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Categories />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/items"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Items />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/modifiers"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Modifiers />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/modifiers/:groupId/options"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <ModifierOptions />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/bundles"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Bundles />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/promotions"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Promotions />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/upsell-campaigns"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <UpsellCampaigns />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/tags"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Tags />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/allergens"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Allergens />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/protein-types"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <ProteinTypes />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/reservations"
          element={<Navigate to="/menu/reservations/create-slots" replace />}
        />
        <Route
          path="/menu/reservations/create-slots"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Reservations section="weeklySlots" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/reservations/exceptions"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Reservations section="exceptions" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/reservations/settings"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <Reservations section="settings" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/day-overrides"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <DayOverrides />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/weekly-scheduling"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <WeeklyScheduling />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Routes - Non-Super Admin Only */}
        <Route
          path="/settings/general"
          element={
            <ProtectedRoute requireNonSuperAdmin requireActiveLicense>
              <MainLayout>
                <GeneralSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/order-types"
          element={
            <ProtectedRoute requireNonSuperAdmin requireActiveLicense>
              <MainLayout>
                <OrderTypes />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/qr-config"
          element={
            <ProtectedRoute requireNonSuperAdmin requireActiveLicense>
              <MainLayout>
                <QRConfig />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/preview"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <MenuPreview />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* User Management Routes - Available to All Authenticated Users */}
        <Route
          path="/reporting/completed-orders"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <CompletedOrders />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/completed-orders/:orderId"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <CompletedOrderDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/sales-summary"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <SalesSummary />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/payment-summary"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <PaymentSummary />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/hourly-demand"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <HourlyDemand />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/top-items"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <TopItemsCategoryMix />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up"
          element={<Navigate to="/reporting/cashing-up/sessions" replace />}
        />
        <Route
          path="/reporting/cashing-up/sessions"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <CashSessionsReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/sessions/:cashSessionId"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <CashSessionDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/variances"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <VariancesReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/variances/:cashSessionId"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <VarianceDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/paid-outs"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <PaidOutsReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/paid-outs/:cashMovementId"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <PaidOutDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/cashing-up/day-closes"
          element={
            <ProtectedRoute requireNonSuperAdmin>
              <MainLayout>
                <DayClosesReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:userId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:userId/roles"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserRoleAssignment />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RoleList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RoleForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles/:roleId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RoleForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles/:roleId/permissions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RolePermissionMatrix />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/permissions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PermissionList />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
